import { FormattedMessage } from 'react-intl';
import {
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import StatusNubs from '@/components/StatusNubs';
import Table from '@/components/Table';
import TableHeaderCell from '@/components/TableHeaderCell';
import SyncButtonWithModalContainer from '@/features/accounting-software/components/SyncButtonWithModalContainer';
import FilterButton from '@/legacy/shared/components/SearchFilter/FilterButton';
import FilterChip from '@/legacy/shared/components/SearchFilter/FilterChips/FilterChip';
import Filter from '@/legacy/shared/components/SearchFilter/models/Filter';
import { Property as FilterProperty } from '@/legacy/shared/components/SearchFilter/models/Property';
import { DisplayDateFormat, formatServerDate } from '@/utils/formatDate';
import { DisplayMoneyFormat, formatMoney } from '@/utils/formatMoney';

import { Row } from './types';

const NotApplicable = (): React.JSX.Element => <FormattedMessage defaultMessage="None Specified" />;

type Props = {
  error: unknown;
  filters: Array<Filter>;
  filterOptions: Array<FilterProperty>;
  isLoading: boolean;
  onClickFilter: (filter: Filter) => void;
  onClickProperty: (property: FilterProperty) => void;
  onCloseFilterPopover: (_: Filter, newFilter: Filter) => void;
  onDeleteFilter: (filter: Filter) => void;
  onPageChange: (page: number) => void;
  page: number;
  rows: Array<Row>;
  totalRecordCount: number;
};

export const PaymentsLogRenderer = ({
  error,
  filters,
  filterOptions,
  isLoading,
  onClickFilter,
  onClickProperty,
  onCloseFilterPopover,
  onDeleteFilter,
  onPageChange,
  page,
  rows,
  totalRecordCount,
}: Props): React.JSX.Element => {
  const pageSizeOptions = [];
  return (
    <Paper sx={{ p: [4, 6] }}>
      <Typography component="h1" gutterBottom variant="h3">
        <FormattedMessage defaultMessage="Payments Received" />
      </Typography>
      <Typography component="p" sx={{ mb: 3 }} variant="body1">
        <FormattedMessage defaultMessage="Your completed and upcoming transactions are listed below." />
      </Typography>
      <Stack direction="row" justifyContent="space-between">
        <SyncButtonWithModalContainer sx={{ mb: 3 }} />
        <FilterButton properties={filterOptions} onClickProperty={onClickProperty} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {filters.map((filter) => (
          <FilterChip
            key={filter.property.name}
            filter={filter}
            onClick={onClickFilter}
            onDelete={onDeleteFilter}
            onClosePopover={onCloseFilterPopover}
          />
        ))}
      </Stack>
      <TableContainer>
        <Table
          currentPageIndex={page}
          isError={Boolean(error)}
          isLoading={isLoading}
          onPageChange={onPageChange}
          pageSizeOptions={pageSizeOptions}
          totalNumberOfRecords={totalRecordCount}
        >
          <TableHead>
            <TableRow>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Company" />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Status" />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Memo" />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Credit Date" />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Est. Availability" />
              </TableHeaderCell>
              <TableHeaderCell>
                <FormattedMessage defaultMessage="Amount" />
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: Row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.companyName ? row.companyName : <NotApplicable />}</TableCell>
                <TableCell>
                  <StatusNubs defaultValue={row.status} />
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: 79,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.memo ? row.memo : <NotApplicable />}
                </TableCell>
                <TableCell>
                  {formatServerDate(row.creditDate, DisplayDateFormat.ShortDate)}
                </TableCell>
                <TableCell>
                  {formatServerDate(row.estimatedAvailability, DisplayDateFormat.ShortDate)}
                </TableCell>
                <TableCell>
                  {formatMoney(row.amount, row.currency, DisplayMoneyFormat.Long)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
